import { clone, find, get, pick } from 'lodash';
import { CHANGE_PROCEDURE } from '@zocdoc-frontend-common/components-proceduredropdown';
import { SchedulingActions } from '../actions/SchedulingActions';
import { INSURANCE_NOT_TAKEN_WARNING } from '../components/AppointmentRequirements/constants';
import { hasRequirement } from '../components/AppointmentRequirements/helpers';
import { BookingStateProperties } from '../constants';

const defaultState = {
    gettingBookingState: false,
    getBookingStateFailure: false,
    getBookingStateSuccess: false,
    updatingBookingState: false,
    updatingIsExistingPatient: false,
    updateBookingStateSuccess: false,
    submittingBookingState: false,
    submitBookingSucceeded: false,
    submitBookingStateFailure: false,
    bookingErrors: [],
    localInSync: false,
};

export const schedulingReducer = (state = defaultState, action) => {
    switch (action.type) {
        case SchedulingActions.RESET_BOOKING_SUBMISSION_STATE: {
            return {
                ...state,
                gettingBookingState: false,
                getBookingStateFailure: false,
                getBookingStateSuccess: false,
                updatingBookingState: false,
                updateBookingStateSuccess: false,
                submittingBookingState: false,
                submitBookingSucceeded: false,
                submitBookingStateFailure: false,
            };
        }

        case SchedulingActions.FETCHING_BOOKING_STATE: {
            return {
                ...state,
                gettingBookingState: true,
                updatingBookingState: false,
                localInSync: false,
            };
        }

        case SchedulingActions.FETCH_BOOKING_STATE_SUCCESS: {
            return {
                ...state,
                gettingBookingState: false,
                getBookingStateSuccess: true,
                bookingState: action.bookingState,
                updatingBookingState: false,
                updateBookingStateSuccess: false,
                localInSync: true,
                errors: undefined,
            };
        }

        case SchedulingActions.FETCH_BOOKING_STATE_FAILURE: {
            const errorFields = [
                ...getErrors(state.errorFields),
                ...getErrors(action.errorFields),
            ];
            const errors = errorFields.length ? errorFields : undefined;
            return {
                ...state,
                gettingBookingState: false,
                getBookingStateSuccess: false,
                updatingBookingState: false,
                updateBookingStateSuccess: false,
                errors,
            };
        }

        case SchedulingActions.UPDATE_LOCAL_BOOKING_STATE: {
            return {
                ...state,
                localInSync: false,
                bookingState: action.bookingState,
            };
        }

        case SchedulingActions.UPDATING_BOOKING_STATE: {
            return {
                ...state,
                localInSync: false,
                updatingBookingState: true,
                gettingBookingState: false,
            };
        }

        case SchedulingActions.UPDATE_BOOKING_STATE_SUCCESS: {
            const { bookingState: oldBookingState } = state;
            const { bookingState: newBookingState } = action;
            // Don't show an OON modal for stale or partially selected plans
            if (
                newBookingState[BookingStateProperties.insurancePlanId] ===
                    null ||
                newBookingState[BookingStateProperties.insurancePlanId] !==
                    oldBookingState[BookingStateProperties.insurancePlanId] ||
                newBookingState[BookingStateProperties.insuranceCarrierId] !==
                    oldBookingState[BookingStateProperties.insuranceCarrierId]
            ) {
                newBookingState.requirements =
                    newBookingState.requirements.filter(
                        (req) => req.key !== INSURANCE_NOT_TAKEN_WARNING
                    );
            }

            return {
                ...state,
                updatingBookingState: false,
                bookingState: {
                    ...newBookingState,
                    ...pick(oldBookingState, [
                        BookingStateProperties.insuranceCarrierId,
                        BookingStateProperties.insurancePlanId,
                    ]),
                },
                updateBookingStateSuccess: true,
                gettingBookingState: false,
                getBookingStateSuccess: false,
                localInSync: true,
                bookingErrors: [],
                errors: undefined,
                bookingErrorCode: undefined,
            };
        }

        case SchedulingActions.UPDATE_BOOKING_STATE_FAILURE: {
            const errorFields = [
                ...getErrors(state.errorFields),
                ...getErrors(action.errorFields),
            ];
            const errors = errorFields.length ? errorFields : undefined;

            return {
                ...state,
                updatingBookingState: false,
                updateBookingStateSuccess: false,
                getBookingStateSuccess: false,
                gettingBookingState: false,
                errors,
            };
        }

        case SchedulingActions.UPDATE_IS_EXISTING_PATIENT_STATE: {
            return {
                ...state,
                updatingIsExistingPatient: true,
            };
        }

        case SchedulingActions.UPDATE_IS_EXISTING_PATIENT_STATE_SUCCESS:
        case SchedulingActions.UPDATE_IS_EXISTING_PATIENT_STATE_FAILURE: {
            return {
                ...state,
                updatingIsExistingPatient: false,
            };
        }

        case CHANGE_PROCEDURE: {
            return {
                ...state,
                bookingState: {
                    ...state.bookingState,
                    [BookingStateProperties.procedureId]: action.procedureId,
                },
            };
        }

        case SchedulingActions.CLEAR_BOOKING_ERROR: {
            return {
                ...state,
                bookingErrors: [],
                submitBookingStateFailure: false,
            };
        }

        case SchedulingActions.SUBMITTING_BOOKING: {
            return {
                ...state,
                submittingBookingState: true,
                bookingErrors: [],
                localInSync: false,
            };
        }

        case SchedulingActions.SUBMIT_BOOKING_SUCCESS: {
            return {
                ...state,
                submittingBookingState: false,
                submitBookingSucceeded: true,
                submitBookingStateFailure: false,
                bookingState: action.bookingState,
                localInSync: true,
            };
        }

        case SchedulingActions.SUBMIT_BOOKING_FAILURE: {
            return {
                ...state,
                submittingBookingState: false,
                submitBookingStateFailure: true,
                bookingErrorCode: action.bookingErrorCode,
                bookingErrors: [
                    ...getErrors(state.bookingErrors),
                    ...getErrors(action.bookingErrors),
                ],
                localInSync: true,
            };
        }

        case SchedulingActions.UPDATE_BOOKING_REQUIREMENT_VALUE: {
            const requirements = get(state.bookingState, 'requirements', []);

            if (hasRequirement(action.key, requirements)) {
                const targetRequirement = find(
                    requirements,
                    (req) => req.key === action.key
                );
                targetRequirement.value = action.value;
            }

            const newBookingState = clone(state.bookingState);
            newBookingState.requirements = requirements;

            return {
                ...state,
                bookingState: newBookingState,
            };
        }

        default:
            return state;
    }
};

const getErrors = (errors) => {
    const bookingErrors = errors || [];
    return bookingErrors.some ? bookingErrors : [errors];
};
